import { useState } from 'react'

import { useUpdateArtifactSubcategory as useUpdateOrgDocumentSubcategory } from '@/api/analyzer.hook'
import { useListSubcategories } from '@/api/analyzer.hook'
import { useUpdateArtifactSubcategory as useUpdateArtifactSubcategoryArtifact } from '@/api/artifact.hook'

import { cn } from '@/lib/ui/style-helpers'

import {
  ConfirmResult,
  useConfirmModal,
} from '@/components/lemmala-design-system/modal/use-confirm-modal'
import { Select } from '@/components/lemmala-design-system/select/select'
import { toast } from '@/components/lemmala-design-system/toast/use-toast'

type SubcategorySelectProps = {
  category: string
  subcategory?: string
  documentId: string
  isPublic: boolean
}

export const SubcategorySelect = ({
  category,
  subcategory,
  documentId,
  isPublic,
}: SubcategorySelectProps) => {
  const { confirm } = useConfirmModal()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { data: subcategories } = useListSubcategories()
  const { mutateAsync: mutateAsyncOrgDocument } = useUpdateOrgDocumentSubcategory()
  const { mutateAsync: mutateAsyncArtifact } = useUpdateArtifactSubcategoryArtifact()
  // The artifact proto carries category/subcategory *names* only, so build display text from them.
  // Kept separate from the Select's `value`, which must be an option key (subcategory id).
  const label = [category, subcategory].filter(Boolean).join(' - ')

  // ListSubcategories returns the same name strings the artifact carries, so the id resolves by
  // matching both. While loading (or if the pair is gone) this is undefined and we fall back to
  // `label`, which InteractiveSelect renders verbatim — so the text never blinks.
  const selectedId = subcategories?.categories.find(
    (option) => option.category === category && option.subcategory === subcategory,
  )?.id

  const showConfirm = async (value: string) => {
    const result = await confirm({
      title: 'Confirm changes?',
      content: (
        <div>
          <h3 className='mb-1'>Adjusting subcategory may:</h3>
          <ul className='list-inside list-disc'>
            <li>Delete existing facts</li>
            <li>Recalculate third-party controls</li>
          </ul>
        </div>
      ),
    })
    if (result === ConfirmResult.CONFIRMED) {
      const mutation = isPublic ? mutateAsyncArtifact : mutateAsyncOrgDocument
      const data = { artifactId: documentId, subCategoryId: value }

      await mutation(data, {
        onSuccess: () => {
          toast({
            title: 'Subcategory updated successfully',
            variant: 'success',
          })
        },
        onError: () => {
          toast({
            title: 'Failed to update subcategory',
            variant: 'destructive',
          })
        },
      })
    }
  }

  return (
    <Select<string>
      popupMatchSelectWidth={false}
      className={cn(
        'truncate text-wrap break-words opacity-0 transition-opacity duration-100 group-hover:opacity-100 [&>.ant-select-selector]:!cursor-pointer [&>.ant-select-selector]:!pl-0',
        {
          'opacity-100': label.length > 0 || isOpen,
        },
      )}
      variant={label.length > 0 ? 'borderless' : 'outlined'}
      onDropdownVisibleChange={setIsOpen}
      showSearch
      placeholder='Choose subcategory'
      onChange={(value) => showConfirm(value)}
      value={selectedId ?? (label || undefined)}
      options={subcategories?.categories.map(({ id, category, subcategory }) => ({
        label: `${category} - ${subcategory}`,
        value: id,
      }))}
    />
  )
}
